import React, {useContext, useRef} from "react"
import AppContext from "../../appContext";
import PagedSearchTable, {PagedTableFunctions} from "../../components/PagedSearchTable";
import Warning, {useWarningState} from "../program/Warning";
import {useNavigate} from "react-router-dom";
import LimeTemplateListResult from "../../controllers/LimeTemplateListResult";
import {dateFormat} from "../../date";
import PermissionEnum from "../../controllers/PermissionEnum";
import {showSuccessOrFailed} from "../../Snacks";
import LimeController from "../../controllers/LimeController";
import {LockClosedIcon} from "@heroicons/react/24/solid";


const LimingTemplates: React.FC = () => {
    const navigate = useNavigate()
    const context = useContext(AppContext)
    const pagedTableRef = useRef<PagedTableFunctions<LimeTemplateListResult>>()
    const warningState = useWarningState<number>(-1)

    function rowRemoveWarning (id: number) {
        warningState.show(`${context.word('removing')}?`, id)
    }

    function rowRemove (id: number) {
        showSuccessOrFailed(context, LimeController.removeTemplate({ id }), 'successfully_deleted', 'failed_to_delete').then(() => {
            pagedTableRef.current?.refresh()
        })
    }

    function copyClicked (event: React.MouseEvent<HTMLSpanElement>, id: number) {
        navigate(`/liming/templates/new?copy=${id}`)
        event.stopPropagation()
        event.preventDefault()
    }

    return <>
        <Warning state={warningState} onYes={rowRemove}/>
        <div className="btn bg-primary m-2" onClick={() => navigate('/liming/templates/new')}>{context.word('new_template')}</div>
        <PagedSearchTable
            rowClick={row => navigate(`/liming/templates/${row.id}`)}
            componentRef={pagedTableRef}
            call={LimeController.templatesPaged}
            keyExtractor={t => t.id} columns={[
            {
                header: context.word('name'),
                row: t => (
                    <span className="flex items-center gap-1">
                        {t.name}
                        {t.hasApprovedPrograms && (
                            <LockClosedIcon className="w-4 h-4 text-gray-400" title="Has approved programs" />
                        )}
                    </span>
                )
            },
            {
                header: context.word('created'),
                row: (t) => (
                    <>
                        <div className="text-l text-gray-800">{t.createdBy}</div>
                        <div className="text-xs text-gray-500">{dateFormat(t.createdOn)}</div>
                    </>
                )
            },
            {
                header: context.word('updated'),
                row: t => dateFormat(t.updateOn)
            },
            {
                header: context.word('submitted'),
                row: t => (
                    <>
                        <div className="text-l text-gray-800">{t.submittedBy || '-'}</div>
                        <div className="text-xs text-gray-500">{t.submittedOn ? dateFormat(t.submittedOn) : ''}</div>
                    </>
                )
            },
            {
                header: context.word('approved'),
                row: t => (
                    <>
                        <div className="text-l text-gray-800">{t.approvedBy || '-'}</div>
                        <div className="text-xs text-gray-500">{t.approvedOn ? dateFormat(t.approvedOn) : ''}</div>
                    </>
                )
            },
            {
                header: context.word('actions'),
                row: t => <>
                    {context.hasPermission(PermissionEnum.LimingProgramsRemovePermission) && !t.hasApprovedPrograms
                        ? <span className="cursor-pointer underline py-2 pl-2 font-medium" onClick={event => {
                            event.stopPropagation()
                            rowRemoveWarning(t.id)
                        }}>{context.word('delete')}</span>
                        : null
                    }
                    <span>&nbsp;|&nbsp;</span>
                    <span onClick={event => copyClicked(event, t.id)} className="cursor-pointer py-2 pr-2 underline font-medium">{context.word('copy')}</span>
                </>
            }
        ]}/>
    </>
}

export default LimingTemplates
